import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { AgentNode, AgentThread, Task, TaskState } from "../types";

// =============================================================================
// Types
// =============================================================================

interface AgentMeshProps {
  agents: AgentNode[];
  threads: AgentThread[];
  tasks: Task[];
}

// =============================================================================
// Constants
// =============================================================================

const ROLE_COLORS: Record<string, string> = {
  orchestrator: "#E6BEFA", // Aurora Lilac
  executor: "#DCA0FA", // Plasma Violet
  validator: "#4aff7a", // hvme-green
  observer: "#F0F0FA", // Soft Cloud
};

const STATUS_COLORS: Record<string, string> = {
  idle: "rgba(240, 240, 250, 0.5)", // Soft Cloud
  working: "#DCA0FA", // Plasma Violet
  complete: "#4aff7a", // hvme-green
  error: "#ff6b7a", // hvme-red
};

const TASK_STATE_CONFIG: Record<
  TaskState,
  { color: string; label: string }
> = {
  pending: { color: "text-hvme-text-muted", label: "Pending" },
  routing: { color: "text-hvme-gold", label: "Routing" },
  executing: { color: "text-hvme-primary", label: "Executing" },
  validating: { color: "text-hvme-gold", label: "Validating" },
  complete: { color: "text-hvme-green", label: "Complete" },
  failed: { color: "text-hvme-red", label: "Failed" },
};

// =============================================================================
// Demo Data (shown when no live agents)
// =============================================================================

const DEMO_AGENTS: AgentNode[] = [
  { id: "orch-1", role: "orchestrator", label: "Orchestrator", status: "idle", x: 0.5, y: 0.2 },
  { id: "exec-1", role: "executor", label: "Executor A", status: "idle", x: 0.2, y: 0.55 },
  { id: "exec-2", role: "executor", label: "Executor B", status: "idle", x: 0.8, y: 0.55 },
  { id: "val-1", role: "validator", label: "Validator", status: "idle", x: 0.5, y: 0.8 },
  { id: "obs-1", role: "observer", label: "Observer", status: "idle", x: 0.15, y: 0.25 },
];

const DEMO_THREADS: AgentThread[] = [
  { sourceId: "orch-1", targetId: "exec-1", status: "idle", direction: "forward" },
  { sourceId: "orch-1", targetId: "exec-2", status: "idle", direction: "forward" },
  { sourceId: "exec-1", targetId: "val-1", status: "idle", direction: "forward" },
  { sourceId: "exec-2", targetId: "val-1", status: "idle", direction: "forward" },
  { sourceId: "obs-1", targetId: "orch-1", status: "idle", direction: "forward" },
];

// =============================================================================
// Canvas Particle Background
// =============================================================================

function ConstellationCanvas({ width, height }: { width: number; height: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = width;
    canvas.height = height;

    // Generate background particles
    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.15,
      vy: (Math.random() - 0.5) * 0.15,
      radius: Math.random() * 1.2 + 0.3,
      opacity: Math.random() * 0.3 + 0.05,
    }));

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      // Update and draw particles
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;

        // Wrap around edges
        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;
        if (p.y < 0) p.y = height;
        if (p.y > height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(220, 160, 250, ${p.opacity})`;
        ctx.fill();
      }

      // Draw faint connection lines between nearby particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const pi = particles[i];
          const pj = particles[j];
          if (!pi || !pj) continue;
          const dx = pi.x - pj.x;
          const dy = pi.y - pj.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 80) {
            ctx.beginPath();
            ctx.moveTo(pi.x, pi.y);
            ctx.lineTo(pj.x, pj.y);
            ctx.strokeStyle = `rgba(220, 160, 250, ${0.04 * (1 - dist / 80)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [width, height]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ width, height }}
    />
  );
}

// =============================================================================
// Agent Node Component
// =============================================================================

function AgentDot({ agent, containerWidth, containerHeight }: {
  agent: AgentNode;
  containerWidth: number;
  containerHeight: number;
}) {
  const x = agent.x * containerWidth;
  const y = agent.y * containerHeight;
  const color = ROLE_COLORS[agent.role] ?? "#9898aa";
  const statusColor = STATUS_COLORS[agent.status] ?? STATUS_COLORS.idle;
  const isActive = agent.status === "working";

  return (
    <motion.g
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 200, damping: 20, delay: Math.random() * 0.3 }}
    >
      {/* Outer glow ring for active agents */}
      {isActive && (
        <motion.circle
          cx={x}
          cy={y}
          r={18}
          fill="none"
          stroke={color}
          strokeWidth={1}
          opacity={0.2}
          animate={{ r: [16, 22, 16], opacity: [0.15, 0.3, 0.15] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
      )}

      {/* Status ring */}
      <circle
        cx={x}
        cy={y}
        r={10}
        fill="none"
        stroke={statusColor}
        strokeWidth={1.5}
        opacity={0.6}
      />

      {/* Core dot */}
      <motion.circle
        cx={x}
        cy={y}
        r={5}
        fill={color}
        style={{ opacity: 0.8 }}
        animate={
          isActive
            ? { opacity: [0.7, 1, 0.7] }
            : { opacity: 0.8 }
        }
        transition={
          isActive
            ? { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
            : {}
        }
      />

      {/* Label */}
      <text
        x={x}
        y={y + 24}
        textAnchor="middle"
        fill="#9898aa"
        fontSize={10}
        fontFamily="Inter, system-ui, sans-serif"
      >
        {agent.label}
      </text>

      {/* Role badge */}
      <text
        x={x}
        y={y + 36}
        textAnchor="middle"
        fill="#5a5a6e"
        fontSize={8}
        fontFamily="JetBrains Mono, monospace"
        style={{ textTransform: "uppercase" }}
      >
        {agent.role}
      </text>
    </motion.g>
  );
}

// =============================================================================
// Thread Line Component
// =============================================================================

function ThreadLine({
  thread,
  agents,
  containerWidth,
  containerHeight,
}: {
  thread: AgentThread;
  agents: AgentNode[];
  containerWidth: number;
  containerHeight: number;
}) {
  const source = agents.find((a) => a.id === thread.sourceId);
  const target = agents.find((a) => a.id === thread.targetId);
  if (!source || !target) return null;

  const x1 = source.x * containerWidth;
  const y1 = source.y * containerHeight;
  const x2 = target.x * containerWidth;
  const y2 = target.y * containerHeight;

  const isActive = thread.status === "active";
  const color = isActive ? "rgba(220, 160, 250, 0.5)" : "rgba(30, 30, 80, 0.6)";

  return (
    <motion.line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke={color}
      strokeWidth={isActive ? 1.5 : 0.8}
      strokeDasharray={isActive ? "none" : "4 4"}
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{
        pathLength: 1,
        opacity: 1,
        ...(isActive ? { strokeWidth: [1, 2, 1] } : {}),
      }}
      transition={{
        pathLength: { duration: 0.8, ease: "easeOut" },
        opacity: { duration: 0.4 },
        strokeWidth: isActive
          ? { duration: 2, repeat: Infinity, ease: "easeInOut" }
          : {},
      }}
    />
  );
}

// =============================================================================
// Task List
// =============================================================================

function TaskList({ tasks }: { tasks: Task[] }) {
  if (tasks.length === 0) {
    return (
      <div className="glass-panel p-6 text-center">
        <p className="text-sm text-hvme-text-muted">
          No tasks yet. Submit a task above to see it flow through the agent mesh.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-medium uppercase tracking-widest text-hvme-text-muted mb-3">
        Task Queue
      </h3>
      <AnimatePresence>
        {tasks.map((task) => {
          const config = TASK_STATE_CONFIG[task.state];
          return (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 12 }}
              className="glass-panel p-4 flex items-center gap-4"
            >
              {/* Progress bar */}
              <div className="w-20 h-1.5 rounded-full bg-hvme-bg-deep overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-hvme-blue"
                  initial={{ width: 0 }}
                  animate={{ width: `${task.progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>

              {/* Description */}
              <span className="flex-1 text-sm text-hvme-text-primary truncate">
                {task.description}
              </span>

              {/* State badge */}
              <span
                className={`text-xs font-mono px-2 py-0.5 rounded ${config.color}`}
              >
                {config.label}
              </span>

              {/* Result or error */}
              {task.result && (
                <span className="text-xs text-hvme-green truncate max-w-[200px]">
                  {task.result}
                </span>
              )}
              {task.error && (
                <span className="text-xs text-hvme-red truncate max-w-[200px]">
                  {task.error}
                </span>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

// =============================================================================
// Agent Mesh (Main Component)
// =============================================================================

export function AgentMesh({ agents, threads, tasks }: AgentMeshProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });

  // Use live data or demo data
  const displayAgents = agents.length > 0 ? agents : DEMO_AGENTS;
  const displayThreads = threads.length > 0 ? threads : DEMO_THREADS;

  // Responsive container sizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        setDimensions({ width, height: Math.min(width * 0.5, 450) });
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Legend items
  const legend = useMemo(
    () =>
      Object.entries(ROLE_COLORS).map(([role, color]) => ({
        role,
        color,
      })),
    [],
  );

  return (
    <div className="space-y-6">
      {/* Mesh Visualization */}
      <div ref={containerRef} className="glass-panel relative overflow-hidden">
        {/* Canvas particle background */}
        <ConstellationCanvas width={dimensions.width} height={dimensions.height} />

        {/* SVG agent constellation */}
        <svg
          width={dimensions.width}
          height={dimensions.height}
          className="relative z-10"
          viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        >
          {/* Thread lines (drawn first, behind nodes) */}
          {displayThreads.map((thread) => (
            <ThreadLine
              key={`${thread.sourceId}-${thread.targetId}`}
              thread={thread}
              agents={displayAgents}
              containerWidth={dimensions.width}
              containerHeight={dimensions.height}
            />
          ))}

          {/* Agent nodes */}
          {displayAgents.map((agent) => (
            <AgentDot
              key={agent.id}
              agent={agent}
              containerWidth={dimensions.width}
              containerHeight={dimensions.height}
            />
          ))}
        </svg>

        {/* Legend */}
        <div className="absolute bottom-3 right-4 flex items-center gap-4 z-20">
          {legend.map(({ role, color }) => (
            <div key={role} className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="text-[10px] text-hvme-text-muted capitalize">
                {role}
              </span>
            </div>
          ))}
        </div>

        {/* Demo mode indicator */}
        {agents.length === 0 && (
          <div className="absolute top-3 left-4 z-20">
            <motion.span
              className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest rounded border border-hvme-border text-hvme-text-muted bg-hvme-bg-deep/80"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Demo View
            </motion.span>
          </div>
        )}

        {/* Subtle corner gradient accents */}
        <div
          className="absolute top-0 left-0 w-40 h-40 pointer-events-none opacity-30"
          style={{
            background: "radial-gradient(circle at top left, rgba(220, 160, 250, 0.15), transparent 70%)",
          }}
        />
        <div
          className="absolute bottom-0 right-0 w-40 h-40 pointer-events-none opacity-30"
          style={{
            background: "radial-gradient(circle at bottom right, rgba(230, 190, 250, 0.15), transparent 70%)",
          }}
        />
      </div>

      {/* Task List */}
      <TaskList tasks={tasks} />
    </div>
  );
}
